import { Component, input, output, Input, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PricingDisplayPlan, PricingDisplayFeatureGroup, PricingDisplayFeature, PricingDisplayRegion } from '../../models';
import { CustomSelectComponent, SelectOption } from '../custom-select/custom-select.component';
// Import sibling component directly to avoid circular dependency via barrel
import { FeaturesGridComponent } from '../features-grid/features-grid.component';
import { PricingStoreService } from '../../services/pricing-store.service';
import { TooltipDirective } from '../tooltip/tooltip.component';
import { StickyClassDirective } from '../../directives';
import { BillingToggleComponent, BillingPeriod } from '../billing-toggle/billing-toggle.component';
import { PlanCardComponent } from '../plan-card/plan-card.component';
import { ResponsiveService } from '../../services/responsive.service';

@Component({
  selector: 'plan-display',
  standalone: true,
  imports: [CommonModule, FeaturesGridComponent, CustomSelectComponent, StickyClassDirective, BillingToggleComponent, PlanCardComponent],
  templateUrl: './plan-display.component.html',
  styleUrls: ['./plan-display.component.scss']
})
export class PlanDisplayComponent {
  private readonly store = inject(PricingStoreService);
  private readonly responsive = inject(ResponsiveService);
  
  /** Reactive mobile/tablet/desktop detection */
  readonly isMobile = this.responsive.isMobile;
  readonly isTablet = this.responsive.isTablet;
  readonly isDesktop = this.responsive.isDesktop;
  readonly isMobileOrTablet = this.responsive.isMobileOrTablet;

  /**
   * Plans derived from the store-selected product to ensure consistency
   * with ProductTabs and other components.
   */
  readonly plansSig = computed<PricingDisplayPlan[]>(() => this.store.selectedProduct()?.plans ?? []);

  /**
   * Back-compat: accept `[plans]` input without using it, to avoid template errors.
   * Plans are now derived from the store-selected product.
   */
  @Input() set plans(_value: PricingDisplayPlan[]) {}

  /** Read selectedRegion from store for reactive pricing calculations */
  readonly selectedRegion = computed(() => this.store.selectedRegion());
  
  /** Read selectedRangedValue from store for reactive pricing calculations */
  readonly selectedRangedValue = computed(() => this.store.selectedRangedValue());
  
  readonly showPopularBadge = input<boolean>(true);
  readonly showYearlyPricing = input<boolean>(false);
  readonly allowRegionSelection = input<boolean>(false);
  readonly allowRangedValueSelection = input<boolean>(false);
  
  /**
   * Unique key to force component recreation
   */
  readonly componentKey = input<string>('default');
  
  /** 
   * Display mode - 'cards' for main pricing view, 'comparison' for modal.
   * Each instance receives its own value from parent.
   */
  readonly displayMode = input<'cards' | 'comparison'>('cards');
  
  /** Feature groups derived from the store-selected product. */
  readonly featureGroupsSig = computed<PricingDisplayFeatureGroup[]>(() => this.store.selectedProduct()?.featureGroups ?? []);
  /** Ungrouped features derived from the store-selected product. */
  readonly ungroupedFeaturesSig = computed<PricingDisplayFeature[]>(() => this.store.selectedProduct()?.ungroupedFeatures ?? []);
  /** Back-compat inputs to avoid errors; values ignored in favor of store. */
  @Input() set featureGroups(_value: PricingDisplayFeatureGroup[]) {}
  @Input() set ungroupedFeatures(_value: PricingDisplayFeature[]) {}

  /**
   * Features mode - 'highlighted' shows limited features, 'full' shows all.
   * Each instance receives its own value from parent.
   */
  readonly featuresMode = input<'highlighted' | 'full'>('highlighted');
  
  // Computed boolean for template @if conditions
  readonly isFullMode = computed(() => this.featuresMode() === 'full');

  readonly onPlanSelect = output<PricingDisplayPlan & { period: BillingPeriod }>();
  readonly onFeatureHighlight = output<PricingDisplayFeature>();
  readonly onRegionChange = output<PricingDisplayRegion>();
  readonly onRangedValueChange = output<number>();

  // Signals for reactive state management
  readonly billingPeriod = signal<'monthly' | 'yearly'>(this.store.defaultPeriod());

  /** Host-provided CTA text and the plan to highlight (both empty by default). */
  readonly ctaLabel = computed(() => this.store.ctaLabel());
  readonly highlightedPlanCode = computed(() => this.store.highlightedPlanCode());

  // Computed properties for template usage
  readonly hasPlans = computed(() => this.plansSig().length > 0);
  readonly showBillingToggle = computed(() => 
    this.showYearlyPricing() && this.plansSig().some(p => p.yearlyPrice)
  );
  readonly billingPeriodText = computed(() => this.billingPeriod() === 'yearly' ? '/year' : '/month');
  
  /**
   * Dynamic grid columns: add 1 for billing toggle in comparison mode
   */
  readonly gridColsClass = computed(() => {
    const mode = this.displayMode();
    const planCount = this.plansSig().length;
    const totalCols = mode === 'comparison' ? planCount + 1 : planCount;

    if (totalCols === 1) return 'grid gap-4';
    if (totalCols === 2) return 'grid gap-4 md:grid-cols-2';
    if (totalCols === 3) return 'grid gap-4 md:grid-cols-2 xl:grid-cols-3';
    if (totalCols === 4) return 'grid gap-4 md:grid-cols-2 lg:grid-cols-4';
    if (totalCols >= 5) return 'grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    return 'grid gap-4 md:grid-cols-2 xl:grid-cols-3';
  });
  
  // Available regions from store (set by parent)
  readonly availableRegions = input<PricingDisplayRegion[]>([]);
  
  // Computed: region options for custom select
  readonly regionOptions = computed<SelectOption[]>(() => {
    return this.availableRegions().map(r => ({
      value: r.code,
      label: r.name,
      sublabel: r.currency
    }));
  });
  
  // Computed: ranged value options for custom select
  readonly rangedValueOptions = computed<SelectOption[]>(() => {
    const product = this.store.selectedProduct();
    if (!product?.rangedValue?.options) return [];
    return product.rangedValue.options.map(opt => ({
      value: opt.value,
      label: opt.name
    }));
  });
  
  // Show region selector when allowed AND regions available
  readonly showRegionSelector = computed(() => 
    this.allowRegionSelection() && this.availableRegions().length > 0
  );
  
  // Show ranged value selector when allowed AND product has ranged options
  readonly showRangedValueSelector = computed(() => 
    this.allowRangedValueSelection() && this.rangedValueOptions().length > 0
  );

  /**
   * Get the ranged value unit name (e.g., "Seat", "User", "License")
   */
  readonly rangedValueName = computed(() => {
    const product = this.store.selectedProduct();
    const count = this.selectedRangedValue() || 1;
    
    if (!product?.rangedValue) return 'Seat';
    
    return count === 1 ? product.rangedValue.singleName : product.rangedValue.pluralName;
  });

  readonly product = computed(() => this.store.selectedProduct());
  readonly productName = computed(() => this.product()?.name || '');
  
  /** Current active section name (updated by scroll observer in features-grid) */
  private readonly _activeSectionName = signal<string>('');
  readonly sectionName = computed(() => this._activeSectionName() || this.product()?.name || '');

  /** Handle active section change from features-grid scroll tracking */
  onActiveSectionChange(sectionName: string) {
    this._activeSectionName.set(sectionName);
  }

  /**
   * Emit the selected plan to parent listeners.
   * Uses current store-driven view for consistency across components.
   */
  selectPlan(plan: PricingDisplayPlan) {
    this.onPlanSelect.emit({ ...plan, period: this.billingPeriod() });
  }

  /**
   * Toggle billing period between monthly and yearly.
   */
  toggleBillingPeriod() {
    this.billingPeriod.set(this.billingPeriod() === 'monthly' ? 'yearly' : 'monthly');
  }
  
  /**
   * Set billing period to specific value.
   */
  setBillingPeriod(period: BillingPeriod) {
    this.billingPeriod.set(period);
  }
  
  /**
   * Handle region selection from custom select
   */
  onRegionSelected(regionCode: string | number) {
    const region = this.availableRegions().find(r => r.code === regionCode);
    if (region) {
      this.store.selectedRegion.set(region);
      this.onRegionChange.emit(region);
    }
  }
  
  /**
   * Handle ranged value selection from custom select
   */
  onRangedValueSelected(value: string | number) {
    const numericValue = typeof value === 'string' ? Number(value) : value;
    this.store.selectedRangedValue.set(numericValue);
    this.onRangedValueChange.emit(numericValue);
  }

  // Helper methods for template
  /**
   * Get the base price before discount (for strikethrough display)
   * This applies priceDisplayRule but NOT yearly discount
   */
  getBasePrice(plan: PricingDisplayPlan): number | null {
    const region = this.selectedRegion();
    const rangedValue = this.selectedRangedValue();
    
    // Look up regional/ranged pricing (server returns monthly prices)
    if (plan.regionsRangedPricing && plan.regionsRangedPricing.length > 0 && region && rangedValue !== undefined) {
      const rangedValueNum = typeof rangedValue === 'string' ? Number(rangedValue) : rangedValue;
      
      const regionalPrice = plan.regionsRangedPricing.find(
        rp => rp.country === region.code && rp.rangedVal === rangedValueNum
      );
      
      if (regionalPrice) {
        const monthlyPrice = regionalPrice.price;
        
        // Apply priceDisplayRule to base monthly price (no yearly discount)
        return this.applyPriceDisplayRule(monthlyPrice, plan, rangedValueNum);
      }
    }
    
    // Fall back to base pricing with priceDisplayRule applied
    const basePrice = plan.monthlyPrice;
    if (basePrice === null || basePrice === undefined) return null;
    
    const rangedValueNum = typeof rangedValue === 'string' ? Number(rangedValue) : rangedValue;
    return this.applyPriceDisplayRule(basePrice, plan, rangedValueNum || 1);
  }
  
  /**
   * Return the display price for the current billing period, considering regional, ranged pricing, and priceDisplayRule.
   */
  getDisplayPrice(plan: PricingDisplayPlan): number | null {
    const period = this.billingPeriod();
    const region = this.selectedRegion();
    const rangedValue = this.selectedRangedValue();
    
    // Look up regional/ranged pricing (server returns monthly prices)
    if (plan.regionsRangedPricing && plan.regionsRangedPricing.length > 0 && region && rangedValue !== undefined) {
      // Ensure rangedValue is a number (convert if string)
      const rangedValueNum = typeof rangedValue === 'string' ? Number(rangedValue) : rangedValue;
      
      const regionalPrice = plan.regionsRangedPricing.find(
        rp => rp.country === region.code && rp.rangedVal === rangedValueNum
      );
      
      if (regionalPrice) {
        let monthlyPrice = regionalPrice.price;
        
        // Apply 20% discount for yearly billing (still showing as monthly rate)
        if (period === 'yearly') {
          monthlyPrice = monthlyPrice * 0.8; // 20% discount on monthly price
        }
        
        // Apply priceDisplayRule
        return this.applyPriceDisplayRule(monthlyPrice, plan, rangedValueNum);
      }
    }
    
    // Fall back to base pricing
    let basePrice = period === 'yearly' && plan.yearlyPrice ? plan.yearlyPrice : plan.monthlyPrice;
    if (basePrice === null || basePrice === undefined) return null;
    
    // Apply priceDisplayRule to base pricing
    const rangedValueNum = typeof rangedValue === 'string' ? Number(rangedValue) : rangedValue;
    return this.applyPriceDisplayRule(basePrice, plan, rangedValueNum || 1);
  }
  
  /**
   * Apply priceDisplayRule to a base price
   * 0 = total price (no change)
   * 1 = per unit (divide by rangedValue)
   * 2+ = divide by that specific value
   */
  private applyPriceDisplayRule(basePrice: number, plan: PricingDisplayPlan, rangedValue: number): number {
    const product = this.store.selectedProduct();
    const rule = product?.priceDisplayRule ?? 0;
    
    // Rule 0: Return total price as-is
    if (rule === 0) {
      return basePrice;
    }
    
    // Rule 1: Divide by rangedValue (per unit pricing) - round to whole number
    if (rule === 1) {
      return Math.round(basePrice / rangedValue);
    }
    
    // Rule 2+: Divide by the specific rule value - keep decimals for accurate display
    if (rule >= 2) {
      return basePrice / rule;
    }
    
    return basePrice;
  }
  
  /**
   * Get the text to display after the price (e.g., "per seat", "for 3 seats", "per 1000 seats")
   */
  getPriceDescriptionText(plan: PricingDisplayPlan): string {
    const product = this.store.selectedProduct();
    if (!product) return '';
    
    const rule = Number(product.priceDisplayRule ?? 0);
    const rangedValue = this.selectedRangedValue();
    const price = this.getDisplayPrice(plan);
    
    // Free plan special case
    if (price === 0 || price === null) {
      if (rangedValue) {
        return `for ${rangedValue} ${rangedValue === 1 ? this.rangedValueName().replace(/s$/, '') : this.rangedValueName()}`;
      }
      return '';
    }
    
    // Rule 1: "per seat" or "per unit"
    if (rule === 1) {
      const singleUnit = this.rangedValueName().replace(/s$/, '').toLowerCase();
      return `per ${singleUnit}`;
    }
    
    // Rule 2+: "per 1000 seats" etc.
    if (rule >= 2) {
      const unitName = this.rangedValueName().toLowerCase();
      return `per ${rule} ${unitName}`;
    }
    
    // Rule 0: No suffix needed (will show "Includes X Seats" from plan-card instead)
    return '';
  }
  
  /**
   * Get the display currency for the current region
   */
  getDisplayCurrency(plan: PricingDisplayPlan): string {
    const region = this.selectedRegion();
    const rangedValue = this.selectedRangedValue();
    
    // Try to get currency from regional pricing first
    if (plan.regionsRangedPricing && plan.regionsRangedPricing.length > 0 && region && rangedValue !== undefined) {
      const rangedValueNum = typeof rangedValue === 'string' ? Number(rangedValue) : rangedValue;
      
      const regionalPrice = plan.regionsRangedPricing.find(
        rp => rp.country === region.code && rp.rangedVal === rangedValueNum
      );
      
      if (regionalPrice) {
        return regionalPrice.currency;
      }
    }
    
    // Fall back to region's currency or plan's default currency
    return region?.currency || plan.currency || 'USD';
  }
  
  /**
   * Get the base ranged value (included seats) for display
   */
  getIncludedSeats(): number {
    const rangedValue = this.selectedRangedValue();
    return rangedValue || 3; // Default to 3 if not specified
  }
  
  /**
   * Calculate additional seat price based on plan and region
   */
  getAdditionalSeatPrice(plan: PricingDisplayPlan): string | null {
    const region = this.selectedRegion();
    const rangedValue = this.selectedRangedValue() || 3;
    
    if (!plan.regionsRangedPricing || plan.regionsRangedPricing.length === 0 || !region) {
      return null;
    }
    
    // Ensure rangedValue is a number
    const rangedValueNum = typeof rangedValue === 'string' ? Number(rangedValue) : rangedValue;
    
    // Find prices for current ranged value and next increment
    const currentPrice = plan.regionsRangedPricing.find(
      rp => rp.country === region.code && rp.rangedVal === rangedValueNum
    );
    
    const nextPrice = plan.regionsRangedPricing.find(
      rp => rp.country === region.code && rp.rangedVal === rangedValueNum + 1
    );
    
    if (currentPrice && nextPrice) {
      const additionalCost = nextPrice.price - currentPrice.price;
      // Use currency from the pricing data for consistency
      return this.formatPrice(additionalCost, currentPrice.currency);
    }
    
    return null;
  }

  /**
   * Get the currency symbol for a given currency code
   */
  getCurrencySymbol(currency: string): string {
    try {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(0);
      
      // Extract just the currency symbol (removes the number)
      return formatted.replace(/[\d\s,]/g, '').trim();
    } catch {
      return currency || 'USD';
    }
  }
  
  /**
   * Format price as number only (no currency symbol)
   */
  formatPriceNumber(price: number | null): string {
    if (price === null || price === undefined) return 'Custom';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  }
  
  /**
   * Format the display price for a plan - shows "FREE" for $0, formatted number otherwise
   */
  formatPriceDisplay(plan: PricingDisplayPlan): string {
    const price = this.getDisplayPrice(plan);
    if (price === null || price === undefined) return 'Custom';
    if (price === 0) return 'FREE';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(price);
  }
  
  /**
   * Format a price in the given currency; returns 'Custom' for null/undefined.
   */
  formatPrice(price: number | null, currency: string): string {
    if (price === null || price === undefined) return 'Custom';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(price);
  }

  /**
   * Compute yearly savings percentage compared to paying monthly.
   */
  getYearlySavings(plan: PricingDisplayPlan): number | null {
    if (!plan.monthlyPrice || !plan.yearlyPrice) return null;
    
    const monthlyYearlyEquivalent = plan.monthlyPrice * 12;
    const savings = monthlyYearlyEquivalent - plan.yearlyPrice;
    const savingsPercentage = (savings / monthlyYearlyEquivalent) * 100;
    
    return Math.round(savingsPercentage);
  }

  // TrackBy functions for performance
  /**
   * Track plans by code for stable rendering in @for.
   */
  trackByPlanCode(index: number, plan: PricingDisplayPlan): string {
    return plan.code;
  }

  /**
   * Return a capped list of features to display for a plan.
   */
  getPlanFeatures(plan: PricingDisplayPlan, limit: number = 99): PricingDisplayPlan['highlightedFeatures'] {
    return plan.highlightedFeatures.slice(0, limit);
  }

  /**
   * Whether the plan has more features than the given limit.
   */
  hasMoreFeatures(plan: PricingDisplayPlan, limit: number = 99): boolean {
    return plan.highlightedFeatures.length > limit;
  }

  /**
   * Count of additional features beyond the display limit.
   */
  getAdditionalFeaturesCount(plan: PricingDisplayPlan, limit: number = 99): number {
    return plan.highlightedFeatures.length - limit;
  }
}
